/*
Author: Jeewantha Karunarathna
Date: 26/03/2025
Copyright © 2024 Infix!. All rights reserved.
*/

import InputLabel from '@/components/Atoms/InputLabel'
import { useUrlType } from '@/components/hooks'
import useResponseId from '@/components/hooks/useResponseId'
import store, { RootState } from '@/reducers/store'
import { useValidate } from '@/state/survey/formSlice'
import {
    BasicAnswerSave,
    useUpdateAnswersSubmit,
} from '@/state/survey/saveAnswerSlice'
import { Utility } from '@/utils'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import poaAnswerDetails from '../../SurveyModel/poaQuestions/poaAnswerDetails'

const SingleCheckBox = (props: any) => {
    const { node, children, name, repeatedElement, onBlur } = props
    const form = useSelector((state: RootState) => state.form)
    const [questionName, setQuestionName] = useState('')
    const validateInputField = useValidate()
    const updateAnswersDispatch = useUpdateAnswersSubmit()
    const { answerResponseId } = useResponseId()
    const hasExecuted = useRef(false)
    const [inputValue, setInputValue] = useState(form[node.name].value)
    const { isPatient } = useUrlType()
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
        if (form[node.name].value !== inputValue) {
            setInputValue(form[node.name].value)
        }
    }, [form, inputValue])

    const getClassForWidth = () => {
        const width = Utility.getWidthFromConfig(node)
        switch (width) {
            case '0.5':
                return 'width-half'
            case '0.75':
                return 'width-75'
            default:
                return 'flex-1'
        }
    }

    const handleOnBlur = () => {
        setTimeout(() => {
            // have tomaccess current state of the form.
            const form = store.getState().form
            if (!isPatient && answerResponseId && !repeatedElement) {
                const answers: BasicAnswerSave[] =
                    poaAnswerDetails.generateQuestionAnswerSubmitFormat(
                        form,
                        node
                    )
                updateAnswersDispatch(answers, answerResponseId)
            }
            if (repeatedElement && onBlur) onBlur()
        }, 50) // or 50ms if needed
    }

    useEffect(() => {
        if (
            !isPatient &&
            name &&
            form[node.name] &&
            questionName != name &&
            !hasExecuted.current
        ) {
            //poa only
            validateInputField(name, form[node.name].value, true)
            setQuestionName(name)
            hasExecuted.current = true
        }
    }, [form, questionName, node, isPatient, name, validateInputField])

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checked = event.target.checked
        const newValue = checked ? 'true' : 'false'
        setInputValue(newValue)
        validateInputField(name, newValue, true)

        if (node.validationDependants) {
            node.validationDependants.forEach((element: string) => {
                validateInputField(element, form[element]?.value, true)
            })
        }
        handleOnBlur()
    }

    return (
        <div
            className={`single-check-box-wrap 
        ${Utility.getClassForWidthPx(node)} 
        question-bottom-space-1
        ${node.name}
        ${Utility.getClassForWidth(node)}  
        ${
            !repeatedElement
                ? `user-input ${getClassForWidth()} ${!isPatient ? 'poa-input' : ''} 
        input-border-top`
                : ''
        }`}
        >
            <div className={`${getClassForWidth()}`}>
                {node.subTitle ? (
                    <InputLabel
                        type="subTitle"
                        label={node.subTitle}
                        labelClasses="input-label"
                    ></InputLabel>
                ) : (
                    ''
                )}
                <label className="checkbox-item">
                    {' '}
                    {node.title}
                    <div
                        className={` input-div ${form[node.name].value === 'true' ? 'input-div-selected' : ''} ${isFocused ? 'input-div-selected  input-div-focused' : ''}  `}
                    >
                        <input
                            type="checkbox"
                            name={name}
                            checked={form[node.name]?.value === 'true'}
                            onChange={handleCheckboxChange}
                            onBlur={() => {
                                handleOnBlur()
                                setIsFocused(false)
                            }}
                            onFocus={() => setIsFocused(true)}
                        />
                        <span className="checkmark"></span>
                    </div>
                </label>
                <div>{children}</div>
            </div>
        </div>
    )
}

export default SingleCheckBox
